import { useState, FormEvent } from 'react';

import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/form/textarea';
import { Label } from '@/components/form/label';

interface LogContactFormProps {
  onSubmit: (comment: string) => Promise<void>;
  disabled?: boolean;
}

const MIN_COMMENT_LENGTH = 10;

const commentSchema = z.string().trim().min(MIN_COMMENT_LENGTH);

export function LogContactForm({ onSubmit, disabled = false }: LogContactFormProps) {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedComment = comment.trim();
  const isValid = trimmedComment.length >= MIN_COMMENT_LENGTH;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const result = commentSchema.safeParse(comment);
    if (!result.success) {
      setError(t('track-ack-comment-min-length', { min: MIN_COMMENT_LENGTH }));
      return;
    }

    setError(null);
    setIsSaving(true);

    try {
      await onSubmit(result.data);
      setComment('');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="contact-comment" required>
          {t('track-ack-log-contact-label')}
        </Label>
        <Textarea
          id="contact-comment"
          name="contact-comment"
          placeholder={t('track-ack-log-contact-placeholder')}
          className="min-h-[100px] mt-1.5"
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
            if (error) setError(null);
          }}
          disabled={disabled || isSaving}
          errorMessage={error ?? undefined}
        />
        <p className="text-xs text-muted-foreground mt-1">
          {t('track-ack-comment-helper', { min: MIN_COMMENT_LENGTH })}
          {trimmedComment.length > 0 &&
            ` ${t('track-ack-comment-entered', { count: trimmedComment.length })}`}
        </p>
      </div>
      <Button type="submit" disabled={!isValid || disabled || isSaving} loading={isSaving}>
        {t('track-ack-save-comment')}
      </Button>
    </form>
  );
}
